import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSignIn } from '@clerk/clerk-react';
import { Loader2, AlertCircle, ArrowLeft } from 'lucide-react';

export default function TwoFAScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, setActive, isLoaded } = useSignIn();
  const pendingEmail = location.state?.email;

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!pendingEmail) navigate('/login');
  }, [pendingEmail, navigate]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleInputChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    setError(null);
    if (digit && index < 5) inputRefs.current[index + 1]?.focus();
    if (digit && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) handleSubmit(fullCode);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length > 0) {
      const newCode = [...code];
      for (let i = 0; i < pastedData.length && i < 6; i++) newCode[i] = pastedData[i];
      setCode(newCode);
      inputRefs.current[Math.min(pastedData.length, 5)]?.focus();
      if (pastedData.length === 6) handleSubmit(pastedData);
    }
  };

  const handleSubmit = async (codeString = null) => {
    const fullCode = codeString || code.join('');
    if (fullCode.length !== 6) { setError('Please enter all 6 digits'); return; }
    if (!isLoaded) return;
    setLoading(true);
    setError(null);
    try {
      const result = await signIn.attemptSecondFactor({ strategy: 'email_code', code: fullCode });
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        // Clerk session is now active — routing handled by App.js
      } else {
        setError(`Verification status: ${result.status}. Please try again.`);
      }
    } catch (err) {
      const e0 = err.errors?.[0];
      const msg = e0?.longMessage || e0?.message || '';
      if (msg.toLowerCase().includes('expired')) {
        setIsExpired(true);
        setError(null);
      } else {
        setError(msg || 'Invalid code. Please try again.');
      }
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleRequestNewCode = () => {
    navigate('/login', { state: { prefillEmail: pendingEmail } });
  };

  const handleBack = () => {
    navigate('/login');
  };

  const codeComplete = code.every(d => d !== '');

  return (
    <div style={{
      minHeight: '100vh', background: '#001a4d',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '32px 24px', fontFamily: "'Inter', sans-serif",
      position: 'relative',
    }} data-testid="2fa-screen">

      {/* Back button */}
      <button onClick={handleBack} style={{
        position: 'absolute', top: '20px', left: '20px',
        width: '38px', height: '38px', background: 'rgba(255,255,255,0.1)',
        border: 'none', borderRadius: '10px', display: 'flex',
        alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
      }} data-testid="2fa-back-btn">
        <ArrowLeft size={18} color="white" />
      </button>

      {/* Logo */}
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <div style={{
          background: '#F5C400', border: '3px solid #111',
          borderRadius: '8px', display: 'inline-block',
          padding: '10px 28px', marginBottom: '16px',
        }}>
          <div style={{
            fontFamily: "'Oswald', sans-serif", fontSize: '36px',
            fontWeight: 700, color: '#111', letterSpacing: '8px',
          }}>VCIN</div>
        </div>
      </div>

      {/* Card */}
      <div style={{
        background: 'white', borderRadius: '16px', padding: '28px 24px',
        width: '100%', maxWidth: '380px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
      }}>
        <div style={{
          fontFamily: "'Oswald', sans-serif", fontSize: '18px',
          fontWeight: 700, color: '#001a4d', letterSpacing: '2px',
          textTransform: 'uppercase', marginBottom: '6px',
        }}>Verify Your Identity</div>
        <p style={{ fontSize: '13px', color: '#7a8aaa', lineHeight: 1.6, marginBottom: '20px' }}>
          Enter the 6-digit code sent to your registered email address. Check your junk/spam folder if you can't find it.
        </p>

        {/* Error */}
        {error && (
          <div style={{
            background: '#fff5f5', border: '1.5px solid #fecaca',
            borderLeft: '4px solid #dc2626', borderRadius: '10px',
            padding: '12px 14px', marginBottom: '16px',
            display: 'flex', alignItems: 'flex-start', gap: '10px',
          }} data-testid="2fa-error">
            <AlertCircle size={15} color="#dc2626" style={{ flexShrink: 0, marginTop: '1px' }} />
            <span style={{ fontSize: '13px', color: '#dc2626', lineHeight: 1.4 }}>{error}</span>
          </div>
        )}

        {/* Expired */}
        {isExpired && (
          <div style={{
            background: '#fffbf0', border: '1px solid #f0d890',
            borderLeft: '3px solid #C9973A', borderRadius: '8px',
            padding: '12px 14px', marginBottom: '16px',
          }} data-testid="2fa-expired">
            <p style={{ fontSize: '13px', color: '#92400E', fontWeight: 600, marginBottom: '6px' }}>Code expired</p>
            <button onClick={handleRequestNewCode} style={{
              background: 'none', border: 'none', padding: 0,
              fontSize: '13px', color: '#001a4d', fontWeight: 600,
              textDecoration: 'underline', cursor: 'pointer',
            }} data-testid="2fa-request-new-code">Tap here to request a new one</button>
          </div>
        )}

        {/* 6-digit inputs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', justifyContent: 'center' }} onPaste={handlePaste}>
          {code.map((digit, index) => (
            <input
              key={index}
              ref={el => inputRefs.current[index] = el}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handleInputChange(index, e.target.value)}
              onKeyDown={e => handleKeyDown(index, e)}
              disabled={loading}
              style={{
                width: '44px', height: '52px', textAlign: 'center',
                fontSize: '22px', fontWeight: 700, color: '#001a4d',
                fontFamily: "'Oswald', sans-serif",
                background: digit ? '#f0f4ff' : '#f7f8fc',
                border: `2px solid ${digit ? '#001a4d' : '#e4e8f0'}`,
                borderRadius: '10px', outline: 'none',
                transition: 'all 0.15s',
              }}
              data-testid={`2fa-digit-${index}`}
            />
          ))}
        </div>

        {/* Verify button */}
        <button
          type="button"
          onClick={() => handleSubmit()}
          disabled={loading || !codeComplete}
          style={{
            width: '100%', height: '50px', border: 'none', borderRadius: '10px',
            background: codeComplete && !loading ? '#001a4d' : '#c8d0e0',
            color: codeComplete && !loading ? 'white' : '#8a9ab8',
            fontFamily: "'Oswald', sans-serif", fontSize: '16px',
            fontWeight: 700, letterSpacing: '4px', textTransform: 'uppercase',
            cursor: codeComplete && !loading ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
            transition: 'all 0.15s',
          }}
          data-testid="2fa-verify-btn"
        >
          {loading
            ? <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Verifying...</>
            : 'Verify Code'
          }
        </button>

        <p style={{ textAlign: 'center', fontSize: '11px', color: '#b0bcd4', marginTop: '14px' }}>
          Code expires in 5 minutes
        </p>
      </div>

      <div style={{
        marginTop: '24px', fontSize: '11px',
        color: 'rgba(255,255,255,0.25)', textAlign: 'center',
      }}>
        Authorised users only · SIA Licensed Personnel
      </div>
    </div>
  );
}
